/**
 * Altitude-Farbskala, angelehnt an tar1090: niedrig = warm (Amber),
 * hoch = kalt (Cyan→Violett). On ground = neutrales Grau.
 */

export const GROUND_COLOR = '#7a8794';
export const UNKNOWN_COLOR = '#9aa4ad';

/** Anzahl Farb-Buckets für vorgerenderte Icons. */
export const ALT_BUCKETS = 14;
export const MAX_ALT_FT = 42_000;

export function altitudeBucket(alt: number | 'ground' | undefined): string {
  if (alt === 'ground') return 'ground';
  if (typeof alt !== 'number' || Number.isNaN(alt)) return 'unknown';
  const clamped = Math.min(Math.max(alt, 0), MAX_ALT_FT - 1);
  return String(Math.floor((clamped / MAX_ALT_FT) * ALT_BUCKETS));
}

export function bucketColor(bucket: string): string {
  if (bucket === 'ground') return GROUND_COLOR;
  if (bucket === 'unknown') return UNKNOWN_COLOR;
  const i = Number(bucket);
  // Hue 45° (Amber) → 290° (Violett); leicht entsättigt, damit hohe Icon-
  // Dichte auf dunkler Basemap ruhiger wirkt und trotzdem klar lesbar bleibt.
  const hue = 45 + (i / (ALT_BUCKETS - 1)) * 245;
  return `hsl(${Math.round(hue)} 72% 60%)`;
}

export function altitudeColor(alt: number | 'ground' | undefined): string {
  return bucketColor(altitudeBucket(alt));
}

export const fmt = {
  alt(alt: number | 'ground' | undefined): string {
    if (alt === 'ground') return 'GND';
    if (typeof alt !== 'number') return '—';
    return `${Math.round(alt).toLocaleString('de-DE')} ft`;
  },
  speed(gs: number | undefined): string {
    return typeof gs === 'number' ? `${Math.round(gs)} kt` : '—';
  },
  track(track: number | undefined): string {
    return typeof track === 'number' ? `${Math.round(track)}°` : '—';
  },
  vrate(rate: number | undefined): string {
    if (typeof rate !== 'number') return '—';
    const sign = rate > 0 ? '+' : '';
    return `${sign}${Math.round(rate)} ft/min`;
  },
  seen(seen: number | undefined): string {
    if (typeof seen !== 'number') return '—';
    return seen < 2 ? 'live' : `vor ${Math.round(seen)} s`;
  },
  duration(s: number | null | undefined): string {
    if (typeof s !== 'number' || s <= 0) return '—';
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);
    return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
  },
  distance(km: number | null | undefined): string {
    if (typeof km !== 'number' || km <= 0) return '—';
    return `${Math.round(km).toLocaleString('de-DE')} km`;
  },
  date(unixS: number): string {
    return new Date(unixS * 1000).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  },
  time(unixS: number): string {
    return new Date(unixS * 1000).toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
    });
  },
};

/**
 * Telemetrie als {value, unit, dir} — erlaubt eigenes Styling für Einheit
 * (gedämpft) und Steig-/Sinkrichtung (Farbe) im Detailpanel.
 */
export const tele = {
  alt(alt: number | 'ground' | undefined): { value: string; unit?: string } {
    if (alt === 'ground') return { value: 'GND' };
    if (typeof alt !== 'number') return { value: '—' };
    return { value: Math.round(alt).toLocaleString('de-DE'), unit: 'ft' };
  },
  speed(gs: number | undefined): { value: string; unit?: string } {
    return typeof gs === 'number' ? { value: String(Math.round(gs)), unit: 'kt' } : { value: '—' };
  },
  vrate(rate: number | undefined): { value: string; unit?: string; dir?: 'up' | 'down' } {
    if (typeof rate !== 'number' || rate === 0) return { value: '—' };
    const dir = rate > 0 ? 'up' : 'down';
    return { value: `${rate > 0 ? '↑' : '↓'} ${Math.abs(Math.round(rate))}`, unit: 'ft/min', dir };
  },
};
